import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { BlogTable, Blog } from "../components/blogs/BlogTable";
import { DeleteBlogDialog } from "../components/blogs/DeleteBlogDialog";
import { blogService } from "../services/blogService";
import { useToast } from "../components/ui/ToastContainer";
import { useDebounce } from "../hooks/useDebounce";

export default function BlogsPage() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState<Blog | null>(null);

  const fetchBlogs = useCallback(async () => {
    try {
      setIsLoading(true);
      const params: any = {
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch,
      };

      if (statusFilter !== "all") params.status = statusFilter;
      if (typeFilter !== "all") params.type = typeFilter;

      const response = await blogService.getAllBlogsAdmin(params);
      setBlogs((response as any).blogs || []);
      setTotalItems((response as any).pagination?.total || 0);
    } catch (error) {
      console.error("Failed to fetch blogs:", error);
      showError("Failed to load blogs. Please refresh the page.");
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    pageSize,
    debouncedSearch,
    statusFilter,
    typeFilter,
    showError,
  ]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, typeFilter]);

  const handleTogglePublish = async (blog: Blog) => {
    try {
      await blogService.updateBlog(blog._id, {
        isPublished: !blog.isPublished,
      });
      showSuccess(
        `Blog ${!blog.isPublished ? "published" : "unpublished"} successfully`
      );
      fetchBlogs();
    } catch (error) {
      console.error("Failed to toggle publish status:", error);
      showError("Failed to update blog status");
    }
  };

  const handleDeleteClick = (blog: Blog) => {
    setSelectedBlog(blog);
    setIsDeleteModalOpen(true);
  };

  return (
    <DashboardLayout>
      <BlogTable
        blogs={blogs}
        description="Write, edit, and publish blog posts and articles."
        isLoading={isLoading}
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        searchTerm={searchTerm}
        statusFilter={statusFilter}
        typeFilter={typeFilter}
        onSearchChange={setSearchTerm}
        onStatusChange={setStatusFilter}
        onTypeChange={setTypeFilter}
        onPageChange={setCurrentPage}
        onAddClick={() => navigate("/blogs/add")}
        onEditClick={(blog) => navigate(`/blogs/edit/${blog._id}`)}
        onDeleteClick={handleDeleteClick}
        onTogglePublish={handleTogglePublish}
      />

      <DeleteBlogDialog
        isOpen={isDeleteModalOpen}
        blog={selectedBlog}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedBlog(null);
        }}
        onSuccess={fetchBlogs}
      />
    </DashboardLayout>
  );
}
